import { DateTime } from '@ksyos/date-time';
import type { DefinedCustomControlData } from '@common/interfaces';
import type {
    GetHookDataRequestBody,
    GetProcessActivityAssetsResponseBody,
    IGetProcessActivityResult,
} from '@common/interfaces/api/processActivitiesApi';
import type { IPatient } from '@common/interfaces/patient';
import type { ProcessActivityCancelValidated } from '@common/validation/processActivityCancel';
import { getDisplayName } from '@common/utils/getDisplayName';
import { fetchJson } from '@frontend/store/fetch';

const baseUrl = '/api/processActivities';

export function downloadPdf(processActivityId: string): void {
    const url = `${baseUrl}/${processActivityId}/downloadPdf`;
    window.open(url);
}

export async function getProcessActivityCustomControlData(processActivityId: string, signal: AbortSignal) {
    const url = `${baseUrl}/${processActivityId}/customControlData`;
    const result = await fetchJson({ method: 'GET', signal, url });

    return result.json;
}

export async function getOneProcessActivity(
    processActivityId: string,
    signal: AbortSignal
): Promise<IGetProcessActivityResult> {
    const url = `${baseUrl}/${processActivityId}`;
    const result = await fetchJson({ method: 'GET', signal, url });

    return result.json?.processActivity;
}

export async function getProcessActivityAssets(
    processActivityId: string,
    signal: AbortSignal
): Promise<GetProcessActivityAssetsResponseBody> {
    const url = `${baseUrl}/${processActivityId}/assets`;
    const result = await fetchJson({ method: 'GET', signal, url });

    return result.json;
}

export async function updateCustomControlData(
    processActivityId: string,
    body: DefinedCustomControlData,
    signal: AbortSignal
): Promise<void> {
    const url = `${baseUrl}/${processActivityId}/customControlData`;
    await fetchJson({ body, method: 'PUT', signal, url });
}

export async function cancelProcessActivity(
    processActivityId: string,
    body: ProcessActivityCancelValidated,
    signal: AbortSignal
): Promise<void> {
    const url = `${baseUrl}/${processActivityId}/cancel`;
    await fetchJson({ body, method: 'PUT', signal, url });
}

export function getProcessActivityHolterDiaryUrl(processActivityId: string, patient: IPatient) {
    const today = DateTime.now().format('DD-MM-YYYY');
    const patientName = getDisplayName(patient);
    const fileName = `Holter dagboek - ${today} - ${patientName}.pdf`;

    return `${baseUrl}/${processActivityId}/makeHolterDiary/${encodeURIComponent(fileName)}`;
}

export async function getHookData({
    hookName,
    requestBody,
    signal,
}: {
    hookName: string;
    requestBody: GetHookDataRequestBody;
    signal: AbortSignal;
}): Promise<Record<string, unknown>> {
    const url = `${baseUrl}/hooks/${hookName}`;

    const response = await fetchJson({
        body: requestBody,
        method: 'POST',
        signal,
        url,
    });

    return response.json;
}
